import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { A11y, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FeedCardPreview } from '../../../components';
import { ConfigContext, OrganisationContext, UserContext } from '../../../context';
import {
  ButtonLinkType,
  ChannelRole,
  Event,
  InAppMessageChannelCreateArgs,
  InAppMessageCreateArgs,
  InAppMessagePlacement,
  OrganisationUsersFiltersSection,
  PushNotificationChannelCreateArgs,
  PushNotificationOrganisationCreateArgs,
  StoryCard,
  StoryCardContentArticlePost,
  StoryCardContentText,
  StoryCardPollContent,
  StoryCardQuoteContent,
  StoryCardThreadContent,
  StoryCardType,
  StoryCardsFeedPayload,
  notificationsApi,
  storyCardApi,
  useEventsQuery,
  useOrganisationUsersFiltersQuery,
  useStoriesByChannelIdQuery,
} from '../../../services';
import {
  Button,
  ButtonType,
  ColorPicker,
  DeeplinkType,
  EventItemPreview,
  FileAcceptType,
  FilePreview,
  FileUpload,
  GalleryItem,
  InputField,
  Modal,
  MultiStepFilter,
  MultiStepFilterData,
  MultiStepFilterItemType,
  MultiStepFilterValue,
  OrderDirection,
  Radio,
  ScheduleInput,
  Select,
  SelectItem,
  TextAreaField,
  getDeepLinkUrl,
  isUrl,
  optArrItem,
  useAppDispatch,
  useAppSelector,
  useIsPersonalizationEnabled,
  useMultiStepFilter,
  useSwiperA11y,
} from '../../../shared';
import { getInAppMessages, getPushNotifications } from '../../../slices';
import { NotificationsType } from '../Notifications';
import { DeeplinkTargetSelect } from './DeeplinkTargetSelect';

import classes from './CreateNotificationModal.module.scss';

interface CreateStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: NotificationsType;
  organisationPushNotificationAllowToCreate: boolean;
  query: string;
  orderDirection: OrderDirection;
  card?: StoryCard;
  event?: Event;
  isOrgLevel?: boolean;
}

const PUSH_TITLE_MIN_LIMIT = 4;
const PUSH_TITLE_MAX_LIMIT = 50;
const PUSH_MESSAGE_MIN_LIMIT = 1;
const PUSH_MESSAGE_MAX_LIMIT = 160;

const INAPP_TITLE_MIN_LIMIT = 4;
const INAPP_TITLE_MAX_LIMIT = 120;
const INAPP_MESSAGE_MIN_LIMIT = 4;
const INAPP_MESSAGE_MAX_LIMIT = 300;

const INAPP_BUTTON_MIN_LIMIT = 2;
const INAPP_BUTTON_MAX_LIMIT = 20;

enum PinContentType {
  NONE = 'NONE',
  URL = 'URL',
  CARD = 'CARD',
  EVENT = 'EVENT',
}

enum PushRecipientType {
  CHANNEL = 'CHANNEL',
  ORG = 'ORG',
}

enum InAppRecipientType {
  CHANNEL = 'CHANNEL',
  ORG = 'ORG',
  USER = 'USER',
}

enum ExpirationHours {
  HALF_DAY = 12,
  DAY = 24,
  TWO_DAYS = 48,
  ONE_WEEK = 168,
  ONE_YEAR = 8760,
}

export const CreateNotificationModal: FunctionComponent<CreateStoryModalProps> = memo(
  ({
    isOpen,
    onClose,
    type,
    organisationPushNotificationAllowToCreate,
    query,
    orderDirection,
    card,
    event,
    isOrgLevel,
  }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const {
      inAppNotifications: {
        textColor: inAppTextColor,
        backgroundColor: inAppBackgroundColor,
        buttonTextColor: inAppButtonTextColor,
        buttonBackgroundColor: inAppButtonBackgroundColor,
      },
      profile: { fields: profileFields },
    } = useContext(ConfigContext).config.elements;

    const isPersonalizationEnabled = useIsPersonalizationEnabled();

    const swiperA11y = useSwiperA11y();

    const navigate = useNavigate();

    const { search } = useLocation();

    const { organisation } = useContext(OrganisationContext);

    const { channelId } = useContext(UserContext).userInfo.userData;

    const { channels } = useAppSelector(({ channels }) => channels);

    const channelsFiltered = useMemo(
      () => channels.filter(({ isArchived }) => !isArchived),
      [channels]
    );

    const [storyCardsFeedTrigger] = storyCardApi.endpoints.storyCardsFeed.useLazyQuery();

    const [pushNotificationChannelCreate] =
      notificationsApi.endpoints.pushNotificationChannelCreate.useMutation();

    const [pushNotificationOrganisationCreate] =
      notificationsApi.endpoints.pushNotificationOrganisationCreate.useMutation();

    const [inAppMessageChannelCreate] =
      notificationsApi.endpoints.inAppMessageChannelCreate.useMutation();

    const [inAppMessageOrganisationCreate] =
      notificationsApi.endpoints.inAppMessageOrganisationCreate.useMutation();

    const isPush = useMemo(() => type === NotificationsType.PUSH, [type]);

    const [pushCard, setPushCard] = useState<StoryCard | undefined>(card);

    const [pushEvent, setPushEvent] = useState<Event | undefined>(event);

    const [loading, setLoading] = useState<boolean>(false);

    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);

    const inputDefaults = useMemo(() => {
      if (pushEvent) {
        return [pushEvent.name, ''];
      }

      if (!pushCard) {
        return ['', ''];
      }

      const { type, content, event } = pushCard;

      switch (type) {
        case StoryCardType.EDITORIAL:
        case StoryCardType.IMAGE:
        case StoryCardType.AUDIO:
        case StoryCardType.VIDEO:
        case StoryCardType.PDF: {
          const { headline, text } = content as StoryCardContentText;
          return [headline, text];
        }
        case StoryCardType.THREAD: {
          const { title } = content as StoryCardThreadContent;
          return [title, ''];
        }
        case StoryCardType.ARTICLE:
        case StoryCardType.POST: {
          const { title, abstract } = content as StoryCardContentArticlePost;
          return [title, abstract];
        }
        case StoryCardType.QUOTE: {
          const { quote, quotePerson, quoteSource } = content as StoryCardQuoteContent;
          return [`${quotePerson} via ${quoteSource?.toLowerCase()}`, quote];
        }
        case StoryCardType.POLL: {
          const { headline, text } = content as StoryCardPollContent;
          return [headline, text];
        }
        case StoryCardType.EVENT:
          return [event?.name ?? '', ''];
        default:
          return ['', ''];
      }
    }, [pushCard, pushEvent]);

    const [titleDefault, messageDefault] = inputDefaults;

    const [title, setTitle] = useState<string>(titleDefault.substring(0, PUSH_TITLE_MAX_LIMIT));

    const [message, setMessage] = useState<string>(
      messageDefault.substring(0, PUSH_MESSAGE_MAX_LIMIT)
    );

    const [storyCardsFeed, setStoryCardsFeed] = useState<StoryCardsFeedPayload | null>(null);

    const [pushRecipient, setPushRecipient] = useState(
      isOrgLevel ? PushRecipientType.ORG : PushRecipientType.CHANNEL
    );

    const pushRecipientTypeItems = useMemo(() => {
      return Object.keys(PushRecipientType)
        .filter((type) => !(type === PushRecipientType.ORG && !organisation.role))
        .map((type) => {
          return { id: type, title: t(`createNotificationModal.recipient-${type}`) };
        });
    }, [organisation.role, t]);

    const [pinContentType, setPinContentType] = useState(PinContentType.NONE);

    const pinContentTypeItems = useMemo(() => {
      return Object.keys(PinContentType)
        .map((type) => {
          return { id: type, title: t(`createNotificationModal.pin-content-${type}`) };
        })
        .filter(
          ({ id }) =>
            pushRecipient === PushRecipientType.CHANNEL ||
            (pushRecipient === PushRecipientType.ORG &&
              id !== PinContentType.CARD &&
              id !== PinContentType.EVENT)
        );
    }, [pushRecipient, t]);

    const [placement, setPlacement] = useState<InAppMessagePlacement>(InAppMessagePlacement.POP_UP);

    const [placementPosition, setPlacementPosition] = useState<number>(1);

    const [backgroundColor, setBackgroundColor] = useState<string>(inAppBackgroundColor);

    const [textColor, setTextColor] = useState<string>(inAppTextColor);

    const [buttonBackgroundColor, setButtonBackgroundColor] = useState<string>(
      inAppButtonBackgroundColor
    );

    const [buttonTextColor, setButtonTextColor] = useState<string>(inAppButtonTextColor);

    const [buttonText, setButtonText] = useState<string>('');

    const [buttonLinkType, setButtonLinkType] = useState<ButtonLinkType>(ButtonLinkType.CANCEL);

    const buttonLinkTypeItems = useMemo(() => {
      return Object.keys(ButtonLinkType).map((type) => {
        return {
          id: type,
          title: t(`createNotificationModal.button-link-${type}`),
          hint: t(`createNotificationModal.button-link-hint-${type}`),
        };
      });
    }, [t]);

    const [expirationHours, setExpirationHours] = useState<number>(ExpirationHours.HALF_DAY);

    const expirationHoursItems = useMemo(() => {
      return Object.values(ExpirationHours)
        .filter((value) => Number(value))
        .map((hr) => {
          return { id: hr, title: t(`createNotificationModal.expiration-hours-${hr}`) };
        });
    }, [t]);

    const [inAppRecipient, setInAppRecipient] = useState(
      isOrgLevel ? InAppRecipientType.ORG : InAppRecipientType.CHANNEL
    );

    const inAppRecipientTypeItems = useMemo(() => {
      return Object.keys(InAppRecipientType)
        .filter((type) =>
          isOrgLevel ? type !== InAppRecipientType.CHANNEL : type !== InAppRecipientType.ORG
        )
        .map((type) => {
          return { id: type, title: t(`createNotificationModal.in-app-recipient-${type}`) };
        });
    }, [isOrgLevel, t]);

    const isUserRecipientType = useMemo(
      () => inAppRecipient === InAppRecipientType.USER,
      [inAppRecipient]
    );

    const { data: orgUsersFilters, isFetching: isFetchingOrgUsersFilters } =
      useOrganisationUsersFiltersQuery(
        {
          filters: {
            sections: Object.keys(
              OrganisationUsersFiltersSection
            ) as OrganisationUsersFiltersSection[],
          },
        },
        { skip: !isUserRecipientType }
      );

    const orgUsersFiltersData = useMemo(() => {
      if (!orgUsersFilters) {
        return [];
      }

      return orgUsersFilters
        .filter(({ values }) => values.length)
        .map(({ name, values }) => {
          const field = name.toLowerCase();

          return {
            title: t(
              profileFields[field as keyof typeof profileFields].localiseKey ?? `common.${field}`
            ),
            items: [
              {
                groupId: field,
                value: null,
                title: t('common.empty'),
                itemType: MultiStepFilterItemType.CHECK,
              },
              ...values.map(({ value }) => {
                return {
                  groupId: field,
                  value,
                  title: value,
                  itemType: MultiStepFilterItemType.CHECK,
                };
              }),
            ],
          };
        });
    }, [orgUsersFilters, profileFields, t]);

    const [userFilter, setUserFilter] = useState<MultiStepFilterValue[]>([]);

    const { getGroupValue } = useMultiStepFilter();

    const userFilterData: MultiStepFilterData = useMemo(() => {
      return {
        title: t('createNotificationModal.filter-setup'),
        items: [
          {
            title: t('createNotificationModal.roles'),
            items: [
              {
                groupId: 'roles',
                value: ChannelRole.EDITOR_LIMITED,
                title: t('roles.editor-limited'),
                itemType: MultiStepFilterItemType.CHECK,
              },
              {
                groupId: 'roles',
                value: ChannelRole.READER,
                title: t('roles.reader'),
                itemType: MultiStepFilterItemType.CHECK,
              },
              {
                groupId: 'roles',
                value: ChannelRole.EDITOR,
                title: t('roles.editor'),
                itemType: MultiStepFilterItemType.CHECK,
              },
              {
                groupId: 'roles',
                value: ChannelRole.ADMIN,
                title: t('roles.admin'),
                itemType: MultiStepFilterItemType.CHECK,
              },
            ],
          },
          {
            title: t('createNotificationModal.user-profile'),
            items: [
              {
                title: t('createNotificationModal.avatar'),
                items: [
                  {
                    groupId: 'avatar',
                    value: true,
                    title: t('createNotificationModal.with-avatar'),
                    itemType: MultiStepFilterItemType.RADIO,
                  },
                  {
                    groupId: 'avatar',
                    value: false,
                    title: t('createNotificationModal.without-avatar'),
                    itemType: MultiStepFilterItemType.RADIO,
                  },
                ],
              },
              ...orgUsersFiltersData,
            ],
          },
          ...optArrItem(Boolean(isOrgLevel), {
            title: t('createNotificationModal.channel'),
            items: channelsFiltered.map(({ id, name }) => {
              return {
                groupId: 'channel',
                value: id,
                title: name,
                itemType: MultiStepFilterItemType.CHECK,
              };
            }),
          }),
        ],
      };
    }, [channelsFiltered, isOrgLevel, orgUsersFiltersData, t]);

    const { data: stories, isFetching: isFetchingStories } = useStoriesByChannelIdQuery(
      { channelId },
      { skip: pinContentType !== PinContentType.CARD }
    );

    const nonEmptyStories = useMemo(
      () => stories?.filter(({ cardsCount }) => Boolean(cardsCount)),
      [stories]
    );

    const [selectedStoryId, setSelectedStoryId] = useState(pushCard?.storyId ?? 0);

    const [selectedCardId, setSelectedCardId] = useState(pushCard?.id ?? 0);

    const storyItems = useMemo(() => {
      return (
        nonEmptyStories?.map(({ id, title }) => {
          return { id, title } as SelectItem;
        }) ?? []
      );
    }, [nonEmptyStories]);

    const { data: eventsData, isFetching: isFetchingEvents } = useEventsQuery(
      { channelId },
      { skip: pinContentType !== PinContentType.EVENT }
    );

    const [selectedEventId, setSelectedEventId] = useState(pushEvent?.id ?? 0);

    const eventItems = useMemo(() => {
      return (
        eventsData?.items.map(({ id, name }) => {
          return { id, title: name } as SelectItem;
        }) ?? []
      );
    }, [eventsData?.items]);

    useEffect(() => {
      if (eventsData?.items.length && !selectedEventId) {
        setSelectedEventId(eventsData.items[0].id);
      }
    }, [eventsData, selectedEventId]);

    useEffect(() => {
      if (pushRecipient === PushRecipientType.ORG) {
        setSelectedStoryId(0);
        setSelectedCardId(0);
        setSelectedEventId(0);

        if ([PinContentType.CARD, PinContentType.EVENT].includes(pinContentType)) {
          setPinContentType(PinContentType.NONE);
        }
      }
    }, [pinContentType, pushRecipient]);

    useEffect(() => {
      if (nonEmptyStories?.length && !selectedStoryId) {
        setSelectedStoryId(nonEmptyStories[0].id);
      }
    }, [nonEmptyStories, selectedStoryId]);

    const getStoryCardsFeed = useCallback(async () => {
      setStoryCardsFeed(null);

      const cardsFeed = await storyCardsFeedTrigger({ storyId: selectedStoryId }).unwrap();

      setStoryCardsFeed(cardsFeed);

      setSelectedCardId(cardsFeed.items[0].id);

      setTimeout(() => {
        document.querySelector('#swiper')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    }, [selectedStoryId, storyCardsFeedTrigger]);

    useEffect(() => {
      if (pushCard || !selectedStoryId) {
        return;
      }

      getStoryCardsFeed();
    }, [getStoryCardsFeed, pushCard, selectedStoryId]);

    const hasStoryCards = useMemo(
      () => Boolean(storyCardsFeed?.items.length),
      [storyCardsFeed?.items.length]
    );

    const [postingTime, setPostingTime] = useState<Date | null>(null);

    const [url, setUrl] = useState<string>('');

    const [urlError, setUrlError] = useState<boolean>(false);

    const [deeplinkTarget, setDeeplinkTarget] = useState<DeeplinkType | null>(null);

    const [imageAspectRatioError, setImageAspectRatioError] = useState<boolean>(false);

    const urlRef = useRef<HTMLInputElement>(null);

    const createButtonLabel = useMemo(() => {
      switch (true) {
        case Boolean(postingTime):
          return t('common.schedule');
        default:
          return t('common.send-now');
      }
    }, [postingTime, t]);

    const createButtonDisabled = useMemo(() => {
      if (isPush) {
        return Boolean(
          title.trim().length < PUSH_TITLE_MIN_LIMIT ||
            message.trim().length < PUSH_MESSAGE_MIN_LIMIT ||
            (pinContentType === PinContentType.URL && urlError) ||
            (pinContentType === PinContentType.CARD && !hasStoryCards) ||
            (pinContentType === PinContentType.EVENT && !selectedEventId)
        );
      }

      return Boolean(
        title.trim().length < INAPP_TITLE_MIN_LIMIT ||
          message.trim().length < INAPP_MESSAGE_MIN_LIMIT ||
          buttonText.trim().length < INAPP_BUTTON_MIN_LIMIT ||
          (buttonLinkType !== ButtonLinkType.CANCEL && urlError) ||
          loading ||
          imageAspectRatioError
      );
    }, [
      buttonLinkType,
      buttonText,
      hasStoryCards,
      imageAspectRatioError,
      isPush,
      loading,
      message,
      pinContentType,
      selectedEventId,
      title,
      urlError,
    ]);

    useEffect(() => {
      if (!pushCard && pinContentType !== PinContentType.CARD) {
        setSelectedStoryId(0);
        setSelectedCardId(0);
      }
    }, [pinContentType, pushCard]);

    useEffect(() => {
      if (!pushEvent && pinContentType !== PinContentType.EVENT) {
        setSelectedEventId(0);
      }
    }, [pinContentType, pushEvent]);

    useEffect(() => {
      if (inAppRecipient !== InAppRecipientType.USER) {
        setUserFilter([]);
      }
    }, [inAppRecipient]);

    useEffect(() => {
      if (urlRef.current) {
        urlRef.current.focus();
      }
    }, [pinContentType, buttonLinkType]);

    useEffect(() => {
      if (galleryItems.length && galleryItems[0].image) {
        const src = galleryItems[0].image.url;
        const image = new Image();
        image.onload = () => {
          setImageAspectRatioError(image.naturalWidth / image.naturalHeight < 0.75);
        };
        image.src = src;
      }

      setImageAspectRatioError(false);
    }, [galleryItems]);

    const urlChangeHandler = useCallback((value: string) => {
      setUrlError(false);
      setUrl(value);
    }, []);

    const urlBlurHandler = useCallback(() => {
      if (!isUrl(url)) {
        setUrlError(true);
      }
    }, [url]);

    const deeplinkTargetChangeHandler = useCallback(
      (target: DeeplinkType | null) => {
        setDeeplinkTarget(target);
        setUrlError(false);
        setUrl(target ? getDeepLinkUrl({ type: target, channelId }) : '');
      },
      [channelId]
    );

    const buttonLinkTypeChangeHandler = useCallback(
      (value: ButtonLinkType) => {
        setButtonLinkType(value);

        if (value !== ButtonLinkType.URL_APP && deeplinkTarget) {
          deeplinkTargetChangeHandler(null);
        }
      },
      [deeplinkTarget, deeplinkTargetChangeHandler]
    );

    const onSlideChangeHandler = useCallback(
      async (activeIndex: number) => {
        if (!storyCardsFeed) {
          return;
        }

        setSelectedCardId(storyCardsFeed.items[activeIndex].id);

        const { items, pageInfo } = storyCardsFeed;

        const { perPage, hasNextPage } = pageInfo;

        const index = activeIndex + 1;

        if (index >= items.length && hasNextPage) {
          const { items, pageInfo } = await storyCardsFeedTrigger({
            storyId: selectedStoryId,
            page: Math.floor(index / perPage) + 1,
            size: perPage,
          }).unwrap();

          setStoryCardsFeed({ items: [...storyCardsFeed.items, ...items], pageInfo });
        }
      },
      [selectedStoryId, storyCardsFeed, storyCardsFeedTrigger]
    );

    const pinContentTypeComponents = useMemo(() => {
      switch (pinContentType) {
        case PinContentType.URL:
          return (
            <InputField
              ref={urlRef}
              value={url}
              label={t('common.url')}
              onChange={({ target }) => urlChangeHandler(target.value)}
              placeholder={t('createNotificationModal.url-placeholder')}
              onBlur={urlBlurHandler}
              {...(urlError && { error: true, errorMessage: t('common.url-invalid') })}
            />
          );
        case PinContentType.EVENT:
          return Boolean(selectedEventId) ? (
            <Select
              label={t('createNotificationModal.pin-content-EVENT')}
              selectedItemId={selectedEventId}
              items={eventItems}
              disabled={isFetchingEvents}
              listMaxHeightRem={12.5}
              onChange={(value) => setSelectedEventId(value as number)}
              staticListPosition
              search
            />
          ) : null;
        case PinContentType.CARD:
          return (
            <>
              {Boolean(selectedStoryId) && (
                <Select
                  label={t('common.story')}
                  selectedItemId={selectedStoryId}
                  items={storyItems}
                  disabled={isFetchingStories}
                  listMaxHeightRem={12.5}
                  onChange={(value) => setSelectedStoryId(value as number)}
                  staticListPosition
                  search
                />
              )}
              {hasStoryCards && (
                <Swiper
                  id={'swiper'}
                  navigation
                  slidesPerView={1}
                  spaceBetween={32}
                  modules={[A11y, Navigation]}
                  {...swiperA11y}
                  style={{ overflow: 'visible' }}
                  onSlideChange={({ activeIndex }) => onSlideChangeHandler(activeIndex)}
                >
                  {storyCardsFeed?.items.map((card) => (
                    <SwiperSlide key={card.id}>
                      <FeedCardPreview card={card} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </>
          );
        default:
          return null;
      }
    }, [
      eventItems,
      hasStoryCards,
      isFetchingEvents,
      isFetchingStories,
      onSlideChangeHandler,
      pinContentType,
      selectedEventId,
      selectedStoryId,
      storyCardsFeed?.items,
      storyItems,
      swiperA11y,
      t,
      url,
      urlBlurHandler,
      urlChangeHandler,
      urlError,
    ]);

    const createNotification = useCallback(async () => {
      onClose();

      try {
        let notification;

        if (isPush) {
          const pushOrgFields: PushNotificationOrganisationCreateArgs = {
            title,
            message,
            url,
            deliveryTime: postingTime?.toISOString(),
          };

          const pushChannelFields: PushNotificationChannelCreateArgs = {
            ...pushOrgFields,
            channelId,
            storyId: selectedStoryId,
            itemId: selectedCardId,
            eventId: selectedEventId,
          };

          notification = await toast.promise(
            (pushRecipient === PushRecipientType.CHANNEL
              ? pushNotificationChannelCreate(pushChannelFields)
              : pushNotificationOrganisationCreate(pushOrgFields)
            ).unwrap(),
            {
              pending: t(`createNotificationModal.create-pending-push`),
              success: t(`createNotificationModal.create-success-push`),
              error: t(`createNotificationModal.create-error-push`),
            }
          );
        } else {
          const avatar = getGroupValue({ filter: userFilter, groupId: 'avatar' });

          const roles = getGroupValue({
            filter: userFilter,
            groupId: 'roles',
            multiCheck: true,
          });

          const department = getGroupValue({
            filter: userFilter,
            groupId: 'department',
            multiCheck: true,
          });

          const position = getGroupValue({
            filter: userFilter,
            groupId: 'position',
            multiCheck: true,
          });

          const channels = getGroupValue({
            filter: userFilter,
            groupId: 'channel',
            multiCheck: true,
          });

          const inAppOrgFields: InAppMessageCreateArgs = {
            title,
            intro: message,
            url,
            textColor,
            bgColor: backgroundColor,
            expirationHours,
            buttonLinkType,
            buttonText,
            buttonTextColor,
            buttonBackgroundColor,
            placement,
            deliveryTime: postingTime?.toISOString(),
            imageId: galleryItems[0]?.image?.id,
            ...(placement === InAppMessagePlacement.FEED && { placementPosition }),
            ...(typeof avatar !== 'undefined' && { avatar }),
            ...(roles?.length && { roles }),
            ...(department?.length && { department }),
            ...(position?.length && { position }),
            ...(channels?.length && { channels }),
          };

          const inAppChannelFields: InAppMessageChannelCreateArgs = {
            ...inAppOrgFields,
            channelId,
          };

          notification = await toast.promise(
            (isOrgLevel
              ? inAppMessageOrganisationCreate(inAppOrgFields)
              : inAppMessageChannelCreate(inAppChannelFields)
            ).unwrap(),
            {
              pending: t(`createNotificationModal.create-pending-in-app`),
              success: t(`createNotificationModal.create-success-in-app`),
              error: t(`createNotificationModal.create-error-in-app`),
            }
          );
        }

        if (!notification) {
          return;
        }

        if (Boolean(search)) {
          navigate('/admin/notifications');
        }

        if (isPush) {
          dispatch(
            getPushNotifications({
              ...(!isOrgLevel && { channelId }),
              filters: { query, excludeOrganisationNotifications: !isOrgLevel },
              orderDirection,
            })
          );
          return;
        }

        dispatch(
          getInAppMessages({
            ...(!isOrgLevel && { channelId }),
            filters: { query, excludeOrganisationMessages: !isOrgLevel },
            orderDirection,
          })
        );
      } catch (_) {}
    }, [
      backgroundColor,
      buttonBackgroundColor,
      buttonLinkType,
      buttonText,
      buttonTextColor,
      channelId,
      dispatch,
      expirationHours,
      galleryItems,
      getGroupValue,
      inAppMessageChannelCreate,
      inAppMessageOrganisationCreate,
      isOrgLevel,
      isPush,
      message,
      navigate,
      onClose,
      orderDirection,
      placement,
      placementPosition,
      postingTime,
      pushNotificationChannelCreate,
      pushNotificationOrganisationCreate,
      pushRecipient,
      query,
      search,
      selectedCardId,
      selectedEventId,
      selectedStoryId,
      t,
      textColor,
      title,
      url,
      userFilter,
    ]);

    const pushCardCancellation = useCallback(() => {
      setPushCard(undefined);
      setSelectedStoryId(0);
      setSelectedCardId(0);
    }, []);

    const pushEventCancellation = useCallback(() => {
      setPushEvent(undefined);
      setSelectedEventId(0);
    }, []);

    const pushLinkedContent = useMemo(() => {
      if (pushEvent) {
        return (
          <div className={classes['create-notification__push-event']}>
            <EventItemPreview event={pushEvent} />
            <button
              type={'button'}
              className={classes['create-notification__cancel']}
              aria-label={t('common.remove')}
              onClick={pushEventCancellation}
            />
          </div>
        );
      }

      if (!pushCard) {
        return (
          <>
            {organisationPushNotificationAllowToCreate && !isOrgLevel && (
              <Select
                label={t('createNotificationModal.recipient')}
                selectedItemId={pushRecipient}
                items={pushRecipientTypeItems}
                onChange={(value) => setPushRecipient(value as PushRecipientType)}
                staticListPosition
              />
            )}

            <Select
              key={pinContentTypeItems.map(({ id }) => id).join(',')}
              label={t('createNotificationModal.pin-content')}
              selectedItemId={pinContentType}
              items={pinContentTypeItems}
              onChange={(value) => setPinContentType(value as PinContentType)}
              staticListPosition
            />

            {pinContentTypeComponents}

            {isOrgLevel && (
              <div className={classes['create-notification__content-org-push-message']}>
                {t('createNotificationModal.org-push-message')}
              </div>
            )}
          </>
        );
      }

      return (
        <div className={classes['create-notification__push-card']}>
          <FeedCardPreview card={pushCard} />
          <button
            type={'button'}
            className={classes['create-notification__cancel']}
            aria-label={t('common.remove')}
            onClick={pushCardCancellation}
          />
        </div>
      );
    }, [
      isOrgLevel,
      organisationPushNotificationAllowToCreate,
      pinContentType,
      pinContentTypeComponents,
      pinContentTypeItems,
      pushCard,
      pushCardCancellation,
      pushEvent,
      pushEventCancellation,
      pushRecipient,
      pushRecipientTypeItems,
      t,
    ]);

    const placementPositionField = useMemo(() => {
      if (placement !== InAppMessagePlacement.FEED) {
        return null;
      }

      return (
        <>
          <InputField
            type={'number'}
            min={1}
            label={t('createNotificationModal.feed-position')}
            value={placementPosition.toString()}
            onChange={({ target: { value } }) => setPlacementPosition(Math.max(1, Number(value)))}
          />

          <span className={classes['create-notification__feed-position-hint']}>
            {t('createNotificationModal.feed-position-hint')}
          </span>
        </>
      );
    }, [placement, placementPosition, t]);

    const placementField = useMemo(() => {
      if (!isPersonalizationEnabled) {
        return null;
      }

      return (
        <div className={classes['create-notification__placement']}>
          <span className={classes['create-notification__placement-label']}>
            {t('createNotificationModal.placement')}
          </span>

          <div className={classes['create-notification__placement-options']}>
            {Object.values(InAppMessagePlacement).map((value) => (
              <Radio
                key={value}
                checked={value === placement}
                name={'notification-placement'}
                label={t(`createNotificationModal.placement-${value}`)}
                onChange={() => setPlacement(value)}
              />
            ))}
          </div>

          {placementPositionField}
        </div>
      );
    }, [isPersonalizationEnabled, placement, placementPositionField, t]);

    const createNotificationContent = useMemo(() => {
      if (isPush) {
        return (
          <>
            <TextAreaField
              label={t('createNotificationModal.title-label')}
              placeholder={t('createNotificationModal.title-placeholder')}
              value={title}
              minLength={PUSH_TITLE_MIN_LIMIT}
              maxLength={PUSH_TITLE_MAX_LIMIT}
              onChange={({ target }) => setTitle(target.value)}
              disableLineBreak
            />
            <TextAreaField
              label={t('createNotificationModal.message-label')}
              placeholder={t('createNotificationModal.message-placeholder')}
              value={message}
              minLength={PUSH_MESSAGE_MIN_LIMIT}
              maxLength={PUSH_MESSAGE_MAX_LIMIT}
              onChange={({ target }) => setMessage(target.value)}
              minRows={2}
              disableLineBreak
            />

            {pushLinkedContent}
          </>
        );
      }

      return (
        <>
          <FileUpload
            galleryItems={galleryItems}
            setGalleryItems={setGalleryItems}
            loading={loading}
            setLoading={setLoading}
            fileAcceptTypes={[FileAcceptType.IMAGE]}
            uploadLimit={1}
          />

          <FilePreview galleryItems={galleryItems} setGalleryItems={setGalleryItems} />

          {imageAspectRatioError && (
            <span className={classes['create-notification__error']}>
              {t('createNotificationModal.image-aspect-ratio-error')}
            </span>
          )}

          <TextAreaField
            label={t('createNotificationModal.title-label')}
            placeholder={t('createNotificationModal.title-placeholder')}
            value={title}
            minLength={INAPP_TITLE_MIN_LIMIT}
            maxLength={INAPP_TITLE_MAX_LIMIT}
            onChange={({ target }) => setTitle(target.value)}
            disableLineBreak
          />

          <TextAreaField
            label={t('createNotificationModal.message-label')}
            placeholder={t('createNotificationModal.message-placeholder')}
            value={message}
            minLength={INAPP_MESSAGE_MIN_LIMIT}
            maxLength={INAPP_MESSAGE_MAX_LIMIT}
            onChange={({ target }) => setMessage(target.value)}
            minRows={2}
            disableLineBreak
          />

          {placementField}

          <div className={classes['create-notification__colors']}>
            <div className={classes['create-notification__colors-item']}>
              <ColorPicker
                value={backgroundColor}
                onChange={setBackgroundColor}
                label={t('createNotificationModal.background-color')}
              />
            </div>
            <div className={classes['create-notification__colors-item']}>
              <ColorPicker
                value={textColor}
                onChange={setTextColor}
                label={t('createNotificationModal.text-color')}
              />
            </div>
          </div>

          <div className={classes['create-notification__colors']}>
            <div className={classes['create-notification__colors-item']}>
              <ColorPicker
                value={buttonBackgroundColor}
                onChange={setButtonBackgroundColor}
                label={t('createNotificationModal.button-background-color')}
              />
            </div>

            <div className={classes['create-notification__colors-item']}>
              <ColorPicker
                value={buttonTextColor}
                onChange={setButtonTextColor}
                label={t('createNotificationModal.button-text-color')}
              />
            </div>
          </div>

          <TextAreaField
            inputClassName={classes['create-notification__input']}
            label={t('createNotificationModal.button-label')}
            placeholder={t('createNotificationModal.button-placeholder')}
            value={buttonText}
            minLength={INAPP_BUTTON_MIN_LIMIT}
            maxLength={INAPP_BUTTON_MAX_LIMIT}
            onChange={({ target }) => setButtonText(target.value)}
            disableLineBreak
          />

          <Select
            label={t('createNotificationModal.button-link')}
            selectedItemId={buttonLinkType}
            items={buttonLinkTypeItems}
            onChange={(value) => buttonLinkTypeChangeHandler(value as ButtonLinkType)}
            staticListPosition
          />

          {buttonLinkType === ButtonLinkType.URL_APP && (
            <DeeplinkTargetSelect value={deeplinkTarget} onChange={deeplinkTargetChangeHandler} />
          )}

          {buttonLinkType !== ButtonLinkType.CANCEL && !deeplinkTarget && (
            <InputField
              ref={urlRef}
              value={url}
              label={t('common.url')}
              onChange={({ target }) => urlChangeHandler(target.value)}
              placeholder={t('createNotificationModal.url-placeholder')}
              onBlur={urlBlurHandler}
              {...(urlError && { error: true, errorMessage: t('common.url-invalid') })}
            />
          )}

          <Select
            label={t('createNotificationModal.expiration-time')}
            selectedItemId={expirationHours}
            items={expirationHoursItems}
            onChange={(value) => setExpirationHours(value as number)}
            staticListPosition
          />

          <Select
            label={t('createNotificationModal.recipient')}
            selectedItemId={inAppRecipient}
            items={inAppRecipientTypeItems}
            onChange={(value) => setInAppRecipient(value as InAppRecipientType)}
            staticListPosition
          />

          {isUserRecipientType && (
            <MultiStepFilter
              key={JSON.stringify(userFilterData)}
              filter={userFilter}
              onChange={setUserFilter}
              data={userFilterData}
              groupIdLocaliseKey={'createNotificationModal'}
              disabled={isFetchingOrgUsersFilters}
            />
          )}
        </>
      );
    }, [
      backgroundColor,
      buttonBackgroundColor,
      buttonLinkType,
      buttonLinkTypeChangeHandler,
      buttonLinkTypeItems,
      buttonText,
      buttonTextColor,
      deeplinkTarget,
      deeplinkTargetChangeHandler,
      expirationHours,
      expirationHoursItems,
      galleryItems,
      imageAspectRatioError,
      inAppRecipient,
      inAppRecipientTypeItems,
      isFetchingOrgUsersFilters,
      isPush,
      isUserRecipientType,
      loading,
      message,
      placementField,
      pushLinkedContent,
      t,
      textColor,
      title,
      url,
      urlBlurHandler,
      urlChangeHandler,
      urlError,
      userFilter,
      userFilterData,
    ]);

    const modalTitle = t('createNotificationModal.add-headline', {
      type: t(`notificationsPage.${type}`),
    });

    const body = useMemo(() => {
      return (
        <div className={classes['create-notification']}>
          <div className={classes['create-notification__title']}>{modalTitle}</div>

          <div className={classes['create-notification__content']}>{createNotificationContent}</div>

          <div className={classes['create-notification__buttons']}>
            <ScheduleInput
              time={postingTime}
              setTime={setPostingTime}
              title={`${t('common.schedule')} ${t(`notificationsPage.${type}`)}`}
              subTitle={t('createNotificationModal.schedule-description', { type })}
              iconHint={t('addContent.schedule-post')}
              summaryLabel={t('common.sending')}
              confirmLabel={t('common.schedule')}
            />
            <Button
              type={ButtonType.primary}
              label={createButtonLabel}
              disabled={createButtonDisabled}
              onClick={createNotification}
            />
          </div>
        </div>
      );
    }, [
      createButtonDisabled,
      createButtonLabel,
      createNotification,
      createNotificationContent,
      modalTitle,
      postingTime,
      t,
      type,
    ]);

    return <Modal isOpen={isOpen} body={body} onClose={onClose} ariaLabel={modalTitle} alignTop />;
  }
);
